// OfferHelp.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  ScrollView,
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  Platform,
  PermissionsAndroid,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import Geolocation from 'react-native-geolocation-service';
import {callApi, getUserId, requestLocationPermission} from '../Helper/Helper';
import {SafeAreaView} from 'react-native-safe-area-context';

const API_BASE_URL = 'https://api.eabu.net';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const OFFER_DETAILS_MAX = 250;

function getOfferEndpoint(sourceRaw) {
  const source = (sourceRaw || '').trim().toLowerCase();
  switch (source) {
    case 'live well':
      return 'offer-live';
    case 'move forward':
      return 'offer-move';
    case 'eat well':
      return 'offer-eat';
    default:
      return 'offer-eat';
  }
}

export default function OfferHelp() {
  const navigation = useNavigation();
  const route = useRoute();

  const { zip, itemId, userId, title, description, from: source } = route.params || {};

  const [currentStep, setCurrentStep] = useState(0);

  const [offerDescription, setOfferDescription] = useState('');
  const [price, setPrice] = useState('');

  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const [coords, setCoords] = useState(null); // { latitude, longitude }
  const [locLoading, setLocLoading] = useState(true);

  const [pUserId, setPUserId] = useState(null);

  const [touched, setTouched] = useState({
    offerDescription: false,
    phoneNumber: false,
    email: false,
    price: false,
  });

  // async function requestLocationPermission() {
  //   if (Platform.OS === 'android') {
  //     try {
  //       const fine = PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION;
  //       const coarse = PermissionsAndroid.PERMISSIONS.ACCESS_COARSE_LOCATION;
  //       const result = await PermissionsAndroid.requestMultiple([fine, coarse]);
  //       const grantedFine = result[fine] === PermissionsAndroid.RESULTS.GRANTED;
  //       const grantedCoarse = result[coarse] === PermissionsAndroid.RESULTS.GRANTED;
  //       return grantedFine || grantedCoarse;
  //     } catch {
  //       return false;
  //     }
  //   } else {
  //     const auth = await Geolocation.requestAuthorization('whenInUse');
  //     return auth === 'granted';
  //   }
  // }

  useEffect(() => {
    (async () => {
      try {
        const id = await getUserId();
        setPUserId(id);
      } catch (e) {
        console.warn('Failed to get user id', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLocLoading(true);
      const hasPermission = await requestLocationPermission();
      if (!hasPermission) {
        Alert.alert('Location', 'Permission denied. Defaulting to 0,0.');
        setCoords({ latitude: 0, longitude: 0 });
        setLocLoading(false);
        return;
      }

      Geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoords({ latitude, longitude });
          setLocLoading(false);
        },
        (error) => {
          Alert.alert(
            'Location error',
            error?.message || 'Unable to get your location. Defaulting to 0,0.'
          );
          setCoords({ latitude: 0, longitude: 0 });
          setLocLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
          forceRequestLocation: true,
          showLocationDialog: true,
        }
      );
    })();
  }, []);

  const phoneDigits = useMemo(
    () => String(phoneNumber || '').replace(/[^\d]/g, ''),
    [phoneNumber]
  );

  const priceNormalized = useMemo(() => {
    const raw = String(price || '').trim();
    if (!raw) return '';
    // allow "12", "12.5", "12.50" and comma input -> dot
    const cleaned = raw.replace(',', '.').replace(/[^0-9.]/g, '');
    // prevent multiple dots
    const parts = cleaned.split('.');
    if (parts.length <= 2) return cleaned;
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }, [price]);

  const errors = useMemo(() => {
    const e = {};
    if (!offerDescription.trim()) e.offerDescription = 'Offer description is required.';
    if (offerDescription.trim().length > OFFER_DETAILS_MAX) {
      e.offerDescription = `Offer description must be ${OFFER_DETAILS_MAX} characters or less.`;
    }
    if (!phoneDigits) e.phoneNumber = 'Phone number is required.';
    else if (phoneDigits.length < 7) e.phoneNumber = 'Enter a valid phone number.';
    if (!email.trim()) e.email = 'Email is required.';
    else if (!EMAIL_RE.test(email.trim())) e.email = 'Enter a valid email.';
    if (priceNormalized && !/^\d+(\.\d{1,2})?$/.test(priceNormalized)) {
      e.price = 'Price must be a number (up to 2 decimals).';
    }
    return e;
  }, [offerDescription, phoneDigits, email, priceNormalized]);

  const isValid = Object.keys(errors).length === 0;

  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleBack = () => setCurrentStep((prev) => Math.max(0, prev - 1));

  const handleNext = () => {
    if (currentStep === 1) {
      markTouched('offerDescription');
      markTouched('price');
      if (errors.offerDescription || errors.price) {
        Alert.alert('Required Fields', 'Please correct the offer details before proceeding.');
        return;
      }
    }

    if (currentStep === 2) {
      markTouched('phoneNumber');
      markTouched('email');
      if (errors.phoneNumber || errors.email) {
        Alert.alert('Required Fields', 'Please correct the contact details before proceeding.');
        return;
      }
    }

    setCurrentStep((prev) => Math.min(3, prev + 1));
  };

  const onSubmit = async () => {
    setTouched({
      offerDescription: true,
      phoneNumber: true,
      email: true,
      price: true,
    });

    if (!isValid) {
      Alert.alert('Validation', 'Please correct the highlighted fields.');
      return;
    }

    try {
      const lat = coords?.latitude ?? 0;
      const lon = coords?.longitude ?? 0;

      if (lat === 0 || lon === 0) {
        Alert.alert(
          'Location required',
          'Your location was not found, please turn on the location in your Phone'
        );
        return;
      }
      setSubmitting(true);

      const payload = {
        zip,
        itemId,
        userId,
        pUserId,
        title,
        description,
        offerDescription: offerDescription.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        price: priceNormalized, // keep as string for backend compatibility
        latitude: coords?.latitude ?? 0,
        longitude: coords?.longitude ?? 0,
      };

      const endpoint = getOfferEndpoint(source);
      await callApi('POST', `${API_BASE_URL}/${endpoint}`, { body: payload });

      Alert.alert('Success', 'Your offer has been submitted.');
      navigation.goBack();
    } catch (e) {
      Alert.alert('Error', e?.message || 'Something went wrong while submitting.');
    } finally {
      setSubmitting(false);
    }
  };

  const ReadonlyBlock = () => (
    <View style={styles.requestCard}>
      <View style={styles.requestCardHeader}>
        <Text style={styles.requestCardTitle}>{title || 'Request Details'}</Text>
        {!!zip && (
          <View style={styles.zipPill}>
            <Text style={styles.zipPillText}>{String(zip)}</Text>
          </View>
        )}
      </View>

      {!!description && (
        <Text style={styles.requestCardDescription}>
          {description}
        </Text>
      )}

      <View style={styles.metaRow}>
        <View style={styles.metaItem}>
          <Text style={styles.metaLabel}>Item ID</Text>
          <Text style={styles.metaValue} numberOfLines={1}>
            {String(itemId ?? '—')}
          </Text>
        </View>



      </View>
      <View style={styles.metaRow}>
        <View style={styles.metaItem}>
          <Text style={styles.metaLabel}>Asked by</Text>
          <Text style={styles.metaValue} numberOfLines={1}>
            {String(userId ?? '—')}
          </Text>
        </View>

        <View style={styles.metaDivider} />

        <View style={styles.metaItem}>
          <Text style={styles.metaLabel}>Offered by</Text>
          <Text style={styles.metaValue} numberOfLines={1}>
            {String(pUserId ?? '—')}
          </Text>
        </View>
      </View>


    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <Text style={styles.title}>Offer Help</Text>

        {/* STEP 1: Request summary */}
        {currentStep === 0 && (
          <View>
            <Text style={styles.sectionHeader}>Step 1: Review the Request</Text>
            <ReadonlyBlock />

            {locLoading && (
              <View style={styles.inlineRow}>
                <ActivityIndicator />
                <Text style={styles.inlineText}>Getting your location…</Text>
              </View>
            )}
          </View>
        )}

        {/* STEP 2: Offer details */}
        {currentStep === 1 && (
          <View>
            <Text style={styles.sectionHeader}>Step 2: Offer Details</Text>
            <ReadonlyBlock />

            <Text style={styles.label}>Offer Description</Text>
            <TextInput
              style={[
                styles.input,
                styles.multiline,
                touched.offerDescription && errors.offerDescription ? styles.inputError : null,
              ]}
              placeholder="Describe how you can help"
              value={offerDescription}
              onChangeText={(t) => setOfferDescription(String(t ?? '').slice(0, OFFER_DETAILS_MAX))}
              onBlur={() => markTouched('offerDescription')}
              multiline
              maxLength={OFFER_DETAILS_MAX}
            />
            <View style={styles.helperRow}>
              <Text style={styles.helperText}>
                {touched.offerDescription && errors.offerDescription ? errors.offerDescription : 'Max 250 characters.'}
              </Text>
              <Text style={styles.counterText}>
                {offerDescription.length}/{OFFER_DETAILS_MAX}
              </Text>
            </View>

            <Text style={styles.label}>Price (optional)</Text>
            <TextInput
              style={[styles.input, touched.price && errors.price ? styles.inputError : null]}
              placeholder="Enter price (e.g., 12.50)"
              value={price}
              onChangeText={setPrice}
              onBlur={() => markTouched('price')}
              keyboardType="decimal-pad"
              inputMode="decimal"
              accessibilityLabel="Price input"
            />
            {touched.price && errors.price ? <Text style={styles.error}>{errors.price}</Text> : null}
          </View>
        )}

        {/* STEP 3: Contact */}
        {currentStep === 2 && (
          <View>
            <Text style={styles.sectionHeader}>Step 3: Contact Information</Text>
            <ReadonlyBlock />

            <Text style={styles.label}>Your Phone Number</Text>
            <TextInput
              style={[
                styles.input,
                touched.phoneNumber && errors.phoneNumber ? styles.inputError : null,
              ]}
              placeholder="Enter your phone number"
              keyboardType="phone-pad"
              value={phoneNumber}
              onChangeText={setPhoneNumber}
              onBlur={() => markTouched('phoneNumber')}
            />
            {touched.phoneNumber && errors.phoneNumber ? (
              <Text style={styles.error}>{errors.phoneNumber}</Text>
            ) : null}

            <Text style={styles.label}>Your Email</Text>
            <TextInput
              style={[styles.input, touched.email && errors.email ? styles.inputError : null]}
              placeholder="Enter your email"
              value={email}
              onChangeText={setEmail}
              onBlur={() => markTouched('email')}
              autoCapitalize="none"
              keyboardType="email-address"
            />
            {touched.email && errors.email ? <Text style={styles.error}>{errors.email}</Text> : null}
          </View>
        )}

        {/* STEP 4: Review & submit */}
        {currentStep === 3 && (
          <View>
            <Text style={styles.sectionHeader}>Step 4: Review & Submit</Text>

            <ReadonlyBlock />

            <View style={styles.reviewItem}>
              <Text style={styles.reviewLabel}>Offer Description</Text>
              <Text style={styles.reviewValue}>{offerDescription || '—'}</Text>
            </View>

            <View style={styles.reviewItem}>
              <Text style={styles.reviewLabel}>Price</Text>
              <Text style={styles.reviewValue}>{priceNormalized ? `$${priceNormalized}` : '—'}</Text>
            </View>

            <View style={styles.reviewItem}>
              <Text style={styles.reviewLabel}>Phone</Text>
              <Text style={styles.reviewValue}>{phoneNumber || '—'}</Text>
            </View>

            <View style={styles.reviewItem}>
              <Text style={styles.reviewLabel}>Email</Text>
              <Text style={styles.reviewValue}>{email || '—'}</Text>
            </View>

            <Pressable
              style={({ pressed }) => [
                styles.button,
                (!isValid || submitting) && styles.buttonDisabled,
                pressed && !submitting && isValid ? styles.buttonPressed : null,
              ]}
              onPress={onSubmit}
              disabled={!isValid || submitting}
              accessibilityRole="button"
              accessibilityState={{ disabled: !isValid || submitting }}
            >
              <Text style={styles.buttonText}>{submitting ? 'Submitting…' : 'Confirm & Submit'}</Text>
            </Pressable>
          </View>
        )}

        {/* NAVIGATION */}
        <View style={styles.navContainer}>
          {currentStep > 0 ? (
            <Pressable onPress={handleBack} style={styles.navButton}>
              <Text style={styles.navButtonText}>Back</Text>
            </Pressable>
          ) : (
            <View />
          )}

          {currentStep < 3 && (
            <Pressable onPress={handleNext} style={[styles.button, styles.navNextButton]}>
              <Text style={styles.buttonText}>Next</Text>
            </Pressable>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  content: { padding: 16, paddingBottom: 32 },

  title: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 16,
    color: '#0d47a1',
    textAlign: 'center',
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: '#546e7a',
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 8,
  },

  label: { fontWeight: '600', marginBottom: 6, color: '#37474f' },
  input: {
    borderWidth: 1,
    borderColor: '#cfd8dc',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
    backgroundColor: '#ffffff',
  },
  multiline: { minHeight: 90, textAlignVertical: 'top' },
  inputError: {
    borderColor: '#e53935',
    backgroundColor: '#fff6f6',
  },
  error: {
    color: '#e53935',
    fontSize: 12,
    marginBottom: 12,
  },

  helperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: -6,
    marginBottom: 12,
  },
  helperText: {
    flex: 1,
    color: '#64748B',
    fontSize: 12,
    paddingRight: 10,
  },
  counterText: {
    color: '#64748B',
    fontSize: 12,
    fontWeight: '700',
  },

  // --- NEW: stylish request card focusing on DETAILS ---
  requestCard: {
    padding: 14,
    borderRadius: 16,
    marginBottom: 16,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#E5E7EB',

    shadowColor: '#0f172a',
    shadowOpacity: 0.08,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },
  requestCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  requestCardTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '800',
    color: '#0F172A',
  },
  zipPill: {
    backgroundColor: '#EEF2FF',
    borderWidth: 1,
    borderColor: '#C7D2FE',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
  },
  zipPillText: {
    color: '#3730A3',
    fontWeight: '800',
    fontSize: 12,
    letterSpacing: 0.3,
  },
  requestCardDescription: {
    fontSize: 15,
    lineHeight: 22,
    color: '#334155',
    marginBottom: 12,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  metaItem: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 2,
  },
  metaDivider: {
    width: 1,
    backgroundColor: '#F1F5F9',
    marginHorizontal: 10,
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: '800',
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0F172A',
  },

  reviewItem: {
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    paddingBottom: 8,
  },
  reviewLabel: {
    fontSize: 12,
    color: '#90a4ae',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  reviewValue: { fontSize: 16, color: '#263238' },

  inlineRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  inlineText: { color: '#475569' },

  navContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  navButton: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    backgroundColor: '#cfd8dc',
    alignItems: 'center',
  },
  navNextButton: { marginTop: 0, minWidth: 120 },
  navButtonText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '600',
  },

  button: {
    marginTop: 8,
    backgroundColor: '#0d47a1',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    minWidth: 160,
  },
  buttonPressed: { opacity: 0.9 },
  buttonDisabled: { backgroundColor: '#90a4ae' },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: '700' },
});
